/**
 * Form state ⇄ API record.
 *
 * One record is one resource is one URL — the model the API has always had, and
 * the model the whole app is built on: a widget points at a resource by record
 * id and reads `connectionData.baseUrl` from it.
 *
 * The editor's list is therefore a list of resources to create, not parts of one
 * resource. Pressing Create posts each of them separately, and each becomes its
 * own row in the Resources list.
 *
 * (An earlier pass grouped several records into one "resource" with modules,
 * tied together by a `connectionData.groupId`. That was dropped: the id was a
 * per-page-load counter, so unrelated resources collided on it and merged in the
 * list. Records already saved carry groupId/groupName/moduleName; nothing reads
 * them any more, and they are harmless where they sit.)
 *
 * Everything the antd form sends is sent unchanged, so a resource saved here
 * still opens in the old modal and still runs in the report editor. The only
 * additions are inside connectionData.requestValues:
 *
 *   bodyMode      none | formData | urlencoded | raw
 *   rawBody       the Raw editor's text
 *   rawFormat     json | xml | html | raw | javascript
 *
 * `bodyParams` is still populated from whichever body table is in use, so a
 * consumer that knows nothing about bodyMode behaves exactly as it does today.
 */

import { DEFAULT_DATE_FORMAT, findType, typeKey } from "./resourceTypes";

/* Local ids for rows the user has added but not saved. Not the API's ids — a
   saved endpoint carries its record id separately, in `resourceId`. */
let seq = 0;
export const localId = (prefix) => `${prefix}-${++seq}`;

export const emptyRow = () => ({ key: "", value: "", dataType: "string" });

/* The config a row gets the moment its type becomes Date. Same defaults as the
   antd form, which builds this inline when the type select changes. */
export const emptyDateConfig = () => ({
  dateOption: "daysBefore",
  timeUnits: 1,
  fixedDate: null,
  dateFormat: DEFAULT_DATE_FORMAT,
});

/* A table always ends in one blank row to type into, and blank rows are dropped
   on save rather than stored as empty pairs. */
const filledRows = (rows) =>
  (Array.isArray(rows) ? rows : []).filter((r) => String(r.key || "").trim() !== "");

/* A date row always arrives with a config to edit. Records written before the
   date options existed — and the antd form's own rows, which store `null` until
   the type select is touched — would otherwise show a config the form has made
   up but the record does not have, and save back without one. */
const withDateConfig = (row) =>
  row.dataType === "date" && !row.dateConfig ? { ...row, dateConfig: emptyDateConfig() } : row;

const withTrailingBlank = (rows) => {
  const list = Array.isArray(rows) && rows.length ? rows.map((r) => withDateConfig({ ...emptyRow(), ...r })) : [];
  const last = list[list.length - 1];
  if (!last || String(last.key || "").trim() !== "" || String(last.value || "").trim() !== "") {
    list.push(emptyRow());
  }
  return list;
};

/**
 * A blank resource — one name, one URL, one set of request values.
 *
 * `name` starts empty rather than "New Resource": it is required, and a
 * placeholder that passes validation is how records called "Untitled Resource"
 * got saved. The sidebar shows "Untitled" for an unnamed draft, but nothing can
 * be created until it has a real name.
 *
 * NOTE — why a new resource starts on WITMEG rather than Public.
 *
 * It is deliberate, and it is the antd form's behaviour rather than the new
 * design's. RestApiForm holds `isWitmegApi` in state initialised to `true`, and
 * only renders its Witmeg API switch when `IsSpecialUser === "true"` — so an
 * ordinary user never saw the control but still posted `isWitmegApi: true`, with
 * `visibleTo: "global"` alongside it. Every REST resource created through that
 * form, by anyone, was therefore stored as a Witmeg API.
 *
 * That pair is what a global board's Resource dropdown filters on
 * (RestApiSelector: `c.isWitmegApi && c.visibleTo === "global"`), so defaulting
 * to Public here would quietly drop every newly created resource out of those
 * dropdowns. Whether the old default was intended or simply an uninitialised
 * `useState(true)` is an open question — kept as it was until a BA or PM says
 * otherwise, because matching today's behaviour is the safe half of the bet and
 * changing it is one line.
 *
 * Auth Required stays `false` here, as it is in the antd form. The rule that a
 * WITMEG API is always authenticated is applied by the form, and only where the
 * scope control is actually on screen — see HttpResourceForm. Forcing it from
 * this side would flip the flag for users who cannot see what caused it.
 */
export const emptyResourceItem = (name = "") => ({
  id: localId("res"),
  /* Which form this belongs to. Changing the connection type re-seeds the page,
     but React renders before that effect runs, so for one frame the new layout
     is holding the old layout's items — and the HTTP form reading `method` off a
     connection is a crash. The layout checks this before rendering anything. */
  kind: "http",
  resourceId: null,
  name,
  method: "GET",
  url: "",
  apiScope: "witmeg", // "public" | "witmeg" → isWitmegApi. See the note above.
  authRequired: false, // → isAuthRequired. The antd form's default too.
  visibleTo: "global",
  auth: { type: "none", params: {} },
  params: [emptyRow()],
  headers: [emptyRow()],
  body: { mode: "none", rawFormat: "json", rawBody: "", formData: [emptyRow()], urlencoded: [emptyRow()] },
});

/* Carries a local `id` like an HTTP draft does, so several connections can be
   filled in on one page and created together. */
export const emptyConnection = (schema) => {
  const values = {};
  schema.fields.forEach((f) => {
    values[f.key] = f.defaultValue || "";
  });
  return {
    id: localId("res"),
    kind: "db", // see emptyResourceItem
    resourceId: null,
    connectionType: schema.connectionMethods ? schema.connectionMethods[0].value : "standard",
    ...values,
  };
};

/* ── Form state → API ────────────────────────────────────────── */

/**
 * The fields every record carries, whatever its type. `typeRecord` is the
 * /resource-type row when it has loaded; the registry fills in for it when it
 * has not, so saving never waits on that call.
 *
 * `description` falls back to "" rather than null because that is what the
 * /resource-type records hold and therefore what the antd forms send.
 *
 * DELIBERATE DIFFERENCE — userId and appId.
 * The antd GraphQL and MongoDB forms send neither: only RestApiForm does. They
 * are sent for every type here because this editor files a resource under an
 * application and the new Resources list has an Application column, an
 * application filter, and a sidebar that lists an application's other
 * resources — all keyed on appId. A Mongo resource saved without one shows no
 * application, cannot be filtered to, and opens with the Application select
 * blank. Everything else matches the old payload per type; this one field is
 * added on purpose. Flagged for the BA.
 */
const identity = (typeMeta, typeRecord, appId) => ({
  icon: typeRecord?.icon || typeMeta.icon,
  category: typeRecord?.category || typeMeta.category || null,
  description: typeRecord?.description || "",
  isActive: true,
  resoueceType: typeMeta.resoueceType,
  userId: localStorage.getItem("userID"),
  appId: appId || null,
});

/**
 * One resource → one create/update payload.
 *
 * Each connection type has its own payload — the antd forms do not share one,
 * and the differences are not cosmetic:
 *
 *   Rest API   carries the ownership and visibility block: isWitmegApi,
 *              isAuthRequired, isShareable, sharedWith, appIdList and (for a
 *              Witmeg API) visibleTo.
 *   GraphQL    carries none of it. RestApiForm is the only form that sends
 *              those fields; GraphQLForm sends the bare record and its
 *              connectionData, nothing else.
 *   MongoDB    the same bare record, with connection details instead of a URL.
 *
 * Sending the Rest API block on a GraphQL record would be inventing ownership
 * the old form never recorded — and isWitmegApi in particular decides who sees
 * a resource in a global board's dropdown.
 *
 * The name goes out as typed, in both `name` and `connectionData.name`, as the
 * antd forms do and as the report editor's Resource dropdown lists it.
 */
export function resourceToPayload({ item, appId, typeMeta, typeRecord }) {
  const bodyRows = item.body.mode === "formData" ? item.body.formData : item.body.urlencoded;
  const name = String(item.name || "").trim();

  const payload = {
    ...identity(typeMeta, typeRecord, appId),
    name,
    connectionData: {
      name,
      baseUrl: item.url,
      authInfo: {
        method: item.auth.type,
        parameters: item.auth.type === "none" ? {} : item.auth.params || {},
      },
      requestValues: {
        httpMethod: String(item.method || "GET").toLowerCase(),
        urlParams: filledRows(item.params),
        requestHeaders: filledRows(item.headers),
        /* Kept in step with the body mode so anything reading bodyParams alone
           still sees the right pairs. Raw and None send none. */
        bodyParams: item.body.mode === "formData" || item.body.mode === "urlencoded" ? filledRows(bodyRows) : [],
        /* Additions, on both HTTP types: the Body tab cannot round-trip without
           them, and a consumer that reads only bodyParams is unaffected. */
        bodyMode: item.body.mode,
        rawFormat: item.body.rawFormat,
        rawBody: item.body.mode === "raw" ? item.body.rawBody : "",
      },
    },
  };

  /* Rest API only — see the note above. GraphQL records carry no ownership or
     visibility fields, so none are invented for them here. */
  if (typeKey(typeMeta.key) === "restapi") {
    const isWitmegApi = item.apiScope === "witmeg";
    payload.appIdList = null;
    /* Whatever the toggle says. The WITMEG-implies-authenticated rule belongs to
       the form, which applies it only where the scope control is visible — a
       user who cannot see the scope should not have this flag changed for them,
       which is exactly what the antd form does today. */
    payload.isAuthRequired = Boolean(item.authRequired);
    payload.isWitmegApi = isWitmegApi;
    payload.isShareable = false;
    payload.sharedWith = null;
    // visibleTo only means anything for a Witmeg API, which is how the old form sends it.
    if (isWitmegApi) payload.visibleTo = item.visibleTo || "global";
  }

  if (item.resourceId) payload.id = item.resourceId;

  return payload;
}

/**
 * One connection → one create/update payload.
 *
 * The bare record and its connection details, matching MongoDBForm exactly. No
 * isWitmegApi, isAuthRequired, isShareable, sharedWith or visibleTo: those
 * belong to a Rest API and the Mongo form has never sent them.
 */
export function connectionToPayload({ connection, appId, typeMeta, typeRecord }) {
  const payload = {
    ...identity(typeMeta, typeRecord, appId),
    name: String(connection.name || "").trim(),
    connectionData: {
      host: connection.host || "",
      port: connection.port || "",
      databaseName: connection.databaseName || "",
      databaseUsername: connection.databaseUsername || "",
      databasePassword: connection.databasePassword || "",
      connectionType: connection.connectionType || "standard",
      requestValues: {},
    },
  };
  if (connection.resourceId) payload.id = connection.resourceId;
  return payload;
}

/* ── API → form state ────────────────────────────────────────── */

export function resourceItemFromRecord(resource) {
  const cd = resource.connectionData || {};
  const rv = cd.requestValues || {};
  const auth = cd.authInfo || {};

  /* Records saved before bodyMode existed have bodyParams and nothing else, so
     a non-empty bodyParams is read as Form Data rather than as no body. */
  const mode = rv.bodyMode || ((rv.bodyParams || []).length ? "formData" : "none");
  const tableRows = withTrailingBlank(rv.bodyParams);

  return {
    id: localId("res"),
    kind: "http",
    resourceId: resource.id || null,
    /* The record's own name wins over connectionData's. They agree on anything
       this editor saved, but a record from the grouped pass has the composed
       "Resource / Module / Endpoint" in `name` and just the endpoint's name in
       `connectionData.name` — and `name` is what the Resources list and the
       report editor's dropdown show, so that is the one to edit. */
    name: resource.name || cd.name || "",
    method: String(rv.httpMethod || "get").toUpperCase(),
    url: cd.baseUrl || "",
    /* A record saved before the flag existed has no isWitmegApi at all, and the
       antd form reads that absence as Witmeg's (`!== undefined ? … : true`).
       Read the same way here, so opening an old resource in either editor shows
       it as the same thing — `resource.isWitmegApi ? …` would quietly demote it
       to Public and save it back that way. */
    apiScope: resource.isWitmegApi === undefined || resource.isWitmegApi ? "witmeg" : "public",
    authRequired: Boolean(resource.isAuthRequired),
    visibleTo: resource.visibleTo || "global",
    auth: { type: auth.method || "none", params: auth.parameters || {} },
    params: withTrailingBlank(rv.urlParams),
    headers: withTrailingBlank(rv.requestHeaders),
    body: {
      mode,
      rawFormat: rv.rawFormat || "json",
      rawBody: rv.rawBody || "",
      formData: mode === "urlencoded" ? [emptyRow()] : tableRows,
      urlencoded: mode === "urlencoded" ? tableRows : [emptyRow()],
    },
  };
}

export function connectionFromResource(resource, schema) {
  const cd = resource.connectionData || {};
  return {
    ...emptyConnection(schema),
    resourceId: resource.id || null,
    name: resource.name || "",
    host: cd.host || "",
    port: cd.port || "",
    databaseName: cd.databaseName || "",
    databaseUsername: cd.databaseUsername || "",
    databasePassword: cd.databasePassword || "",
    connectionType: cd.connectionType || "standard",
  };
}

export const resourceTypeRecord = (resourceTypes, key) =>
  (resourceTypes || []).find((rt) => typeKey(rt.icon) === typeKey(key) || typeKey(rt.name) === typeKey(key)) || null;

export const typeMetaFor = (key) => findType(key) || findType("restapi");
